/**
 * PlayerSettingsPanel — 설정 톱니바퀴 클릭 시 PlayerBar 위에 뜨는 overlap popover.
 * 내용: 배속 chip row (0.5 / 0.75 / 1 / 1.25 / 1.5 / 2x).
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const
type PlaybackRate = typeof PLAYBACK_RATES[number]

interface Props {
  open: boolean
  playbackRate: number
  speedPolicy: 'always' | 'after_first_watch'
  videoComponentEverCompleted: boolean
  onClose: () => void
  onSelectRate: (rate: number) => void
}

function isRateEnabled(rate: number, policy: Props['speedPolicy'], everCompleted: boolean): boolean {
  if (rate <= 1.0) return true
  return policy === 'always' || everCompleted
}

export default function PlayerSettingsPanel({
  open, playbackRate, speedPolicy, videoComponentEverCompleted, onClose, onSelectRate,
}: Props) {
  const { t } = useTranslation('learner')
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose()
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('video.speed') as string}
      className="absolute bottom-[calc(100%+12px)] right-[52px] z-30 w-60 bg-white rounded-[16px] p-4 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.2)]"
    >
      <div className="text-[14px] font-light tracking-[0.5px] uppercase text-lc-gray-500 mb-3">
        {t('video.speed')}
      </div>

      <div className="flex flex-wrap gap-2">
        {PLAYBACK_RATES.map((rate) => {
          const enabled = isRateEnabled(rate, speedPolicy, videoComponentEverCompleted)
          const selected = Math.abs(rate - playbackRate) < 0.001
          const label = rate === 1 ? '1x' : `${rate}x`
          const title = enabled ? undefined : t('video.speedLockedFirstWatch') as string

          return (
            <button
              key={rate}
              type="button"
              onClick={() => enabled && onSelectRate(rate)}
              disabled={!enabled}
              aria-disabled={!enabled}
              title={title}
              className={[
                'h-9 px-4 rounded-[50px] text-[16px] font-medium tracking-[-0.6px] transition-colors',
                selected
                  ? 'bg-lc-lightblue-900 text-white'
                  : enabled
                    ? 'bg-lc-gray-100 text-lc-gray-600 hover:bg-lc-gray-200'
                    : 'bg-lc-gray-100 text-lc-gray-400 cursor-not-allowed',
              ].join(' ')}
            >
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export type { PlaybackRate }
